// App-shell error boundary. Without one, any render throw inside a routed
// screen unmounts the whole React tree and the operator sees a blank page with
// no way back. This catches the throw, renders a recoverable fallback, and
// offers a reset that remounts the subtree.
//
// Class component by necessity: componentDidCatch / getDerivedStateFromError
// have no hook equivalent in React 19.
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

const ID = 'em-mgr--shell--error-boundary';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional hook for tests / telemetry; called with the caught error. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  // Remount the subtree; a transient render throw (bad payload that has since
  // been refetched) recovers without a full page reload.
  handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div
        data-id={ID}
        className="rounded-lg border border-red-300 bg-red-50 p-6 dark:border-red-800 dark:bg-red-950/40"
      >
        <h2
          data-id={`${ID}--title`}
          className="text-base font-semibold text-red-800 dark:text-red-300"
        >
          Something broke while rendering this screen.
        </h2>
        <p
          data-id={`${ID}--message`}
          className="mt-1 break-words font-mono text-sm text-red-700 dark:text-red-400"
        >
          {error.message || 'Unknown error'}
        </p>
        <button
          type="button"
          data-id={`${ID}--reset`}
          onClick={this.handleReset}
          className="mt-4 rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40"
        >
          Try again
        </button>
      </div>
    );
  }
}
